import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DateFormatPipe } from '../pipes/date-format.pipe';
import { EnvironmentVariablesService } from './environment-variables.service';
import { InvestorMeta } from '../models/classes/investor-meta.class';
import dayjs from 'dayjs'
import { InvestorsStorageService } from './investors-storage.service';
import { CookieService } from 'ngx-cookie-service';
import { UserService } from './user.service';
import { Breadcrumb, BreadcrumbParentData } from '../models/interfaces/breadcrumb.interface';
@Injectable({
  providedIn: 'root'
})
export class BreadcrumbDataService {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private datePipe = inject(DateFormatPipe);
  private evService = inject(EnvironmentVariablesService);
  private investorsStorage = inject(InvestorsStorageService);
  private cookieService = inject(CookieService);
  private userService = inject(UserService);

  investorsDropdownData: InvestorMeta[] = [];

  constructor() {
    this.investorsStorage.getInvestors().subscribe((res: InvestorMeta[] | null) => {
      if (!res || res.length == 0) return;
      this.investorsDropdownData = res;
    })
  }

  // When user changes investor from investor dropdown from any page this will get called
  redirectToDashboard(selectedInvestorId: number) {
    const selectedReportingPeriod = this.getReportingPeriodForSelectedInvestor(selectedInvestorId) ?? this.getReportingPeriodValue();
    this.redirectToPage('/investor/' + selectedInvestorId, { 'reportingdate': selectedReportingPeriod });
    this.investorsStorage.setActiveInvestor(selectedInvestorId);
  }

  getReportingPeriodForSelectedInvestor(selectedInvestorId: number) {
    const currentReportingPeriod = this.activatedRoute.snapshot.queryParams.reportingdate;
    if (currentReportingPeriod) {
      const reportingPeriods = Array.from(new Set(this.investorsDropdownData.filter((i: InvestorMeta) => i.id === Number(selectedInvestorId)).flatMap((i: InvestorMeta) => i.reportingPeriods)));
      //do not show reporting periods prior to set hideDatesPriorDate
      const filteredReportingPeriods = reportingPeriods.filter(period => !dayjs(period).isBefore(dayjs(this.evService.hideDatesPriorToDate))).map(period => period.slice(0, 10))
      const isNotCurrentPeriod = (d: string) => d !== currentReportingPeriod;
      return (reportingPeriods.length > 0 && filteredReportingPeriods?.every(isNotCurrentPeriod)) ? filteredReportingPeriods[0] : currentReportingPeriod;
    }
    return null;
  }

  getQueryParamsForBreadcrumbs(breadcrumb: Breadcrumb): Record<string, string> {
    let reportingPeriod: string;
    const queryParams: Record<string, string> = {};
    if (breadcrumb.name === 'Dashboard') {
      reportingPeriod = this.getReportingPeriodForSelectedInvestor(breadcrumb.id) ?? this.getReportingPeriodValue();
    } else {
      reportingPeriod = this.getReportingPeriodValue();
    }
    queryParams['reportingdate'] = reportingPeriod;
    return queryParams;
  }

  redirectToPage(route: string, params: Record<string, string | null>) {
    this.router.navigate([route], { queryParams: params });
  }

  getReportingPeriodValue() {
    let reportingPeriod = this.activatedRoute?.snapshot?.queryParams?.reportingdate;
    if (document.getElementById('asOfDateSel')) {
      const date = this.datePipe.transform((document.getElementById('asOfDateSel') as HTMLInputElement).value, 'yyyy-MM-dd')
      reportingPeriod = date && date !== 'N/A' ? date : reportingPeriod;
    }
    return reportingPeriod;
  }

  getAssetName(assetsDataForBreadcrumb: { id: number; name: string }[], assetId: number) {
    const asset = assetsDataForBreadcrumb.filter(asset => asset.id == assetId)[0]
    if (asset) {
      return asset.name
    }
  }

  getDashboardBreadcrumb(queryParamsInvestorId: number): Breadcrumb[] {
    const breadcrumbs: Breadcrumb[] = [];
    breadcrumbs.push(
      {
        id: queryParamsInvestorId,
        name: 'Dashboard',
        route: '/investor/' + queryParamsInvestorId,
        type: 'Dashboard'
      });
    // If external user, do not show portfolio breadcrumb
    if (!this.userService.isExternalUser()) {
      const portfolioBreadcrumb = this.getPortfolioBreadcrumb(queryParamsInvestorId);
      if (portfolioBreadcrumb) {
        breadcrumbs.push(portfolioBreadcrumb);
      }
    }
    return breadcrumbs;
  }

  getPortfolioBreadcrumb(queryParamsInvestorId: number): Breadcrumb | null {
    const portfolioId = Number(this.cookieService.get('dashboardPortfolioId'));
    if (portfolioId) {
      return {
        id: portfolioId,
        name: 'Portfolio Dashboard',
        route: `/investor/${queryParamsInvestorId}/portfolio/${portfolioId}`,
        type: 'Portfolio'
      };
    }
    return null;
  }

  getPortfolioSeriesBreadcrumbs(queryParamsInvestorId: number, queryParamsSeriesId: number, parentData: BreadcrumbParentData): Breadcrumb {
    return {
      id: parentData.id,
      name: parentData.parent.name + ': ' + parentData.name,
      route: `/investor/${queryParamsInvestorId}/series/${queryParamsSeriesId}`,
      type: 'Series',
    }
  }

  getUnderlyingPortfolioSeriesBreadcrumbs(parentData: BreadcrumbParentData): Breadcrumb {
    return {
      id: parentData.id,
      name: parentData.name,
      type: 'UnderlyingSeries',
      isDisabled: true
    }
  }

  getDealBreadcrumbs(queryParamsInvestorId: number, queryParamsSeriesId: number, queryParamsHoldingId: number, parentData: BreadcrumbParentData)
  : Breadcrumb {
    return {
      id: parentData.id,
      name: parentData.name,
      route: `/investor/${queryParamsInvestorId}/series/${queryParamsSeriesId}/deal/${queryParamsHoldingId}`,
      type: 'Deal'
    }
  }
}